import { Link } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import logoFullWhite from '../assets/images/logo/swarmia-logo-full-white.svg'
import logoFull from '../assets/images/logo/swarmia-logo-full.svg'
import theme from '../styles/theme'
import { HeaderButtons } from './HeaderButtons'
import { MenuToggle } from './MenuToggle'
import DesktopMenu from './menu/DesktopMenu'
import MobileMenu from './menu/MobileMenu'
import Block from './primitives/Block'
import Box from './primitives/Box'
import Row from './primitives/Row'
import GlobalStyleReset from './system/GlobalStyleReset'

type Props = {
  variant?: 'dark' | 'light'
}

const headerHeight = 64
const headerSafeSpace = { xs: 8, md: 24 } as const
// Magic number to make sure all menu items fit on desktop
const headerCss = `
  letter-spacing: 0;
  top: 0;
  width: 100%;
  z-index: 15 !important; // need to fight this, sorry: https://github.com/swarmia/website/blob/072c07a/src/assets/sass/base/_page.scss#L58

  @media screen and (max-width: ${theme.breakpoints.desktopMenu}) {
    max-height: 100vh;
    /* Avoid Chrome from seeing Safari hack */
    @supports (-webkit-touch-callout: none) {
      & {
        /* Hack for Safari hiding content below top & bottom bar */
        max-height: -webkit-fill-available;
      }
    }
  }

  @media (min-width: ${theme.breakpoints.desktopMenu}) {
    .menu-toggle {
      display: none;
    }
  }
`

const Header = ({ variant = 'light' }: Props) => {
  const [active, setActive] = useState(false)
  const toggleMenu = () => {
    setActive(!active)
  }

  const { ref, inView: heroInView } = useInView({
    initialInView: variant === 'dark',
    // Default to light version, if intersection observer is not available
    fallbackInView: false,
    // Switch the color when the bottom of header text hits the hero bottom
    rootMargin: `-${headerHeight - 20}px`,
  })
  useEffect(() => {
    ref(document.querySelector('.hero-block, .hero'))
  }, [ref])

  const { ref: relativeRef, inView: isStatic } = useInView({
    threshold: 1,
    initialInView: true,
  })

  const isDark = variant === 'dark' && heroInView && !active
  return (
    <GlobalStyleReset>
      <Banner />
      <Box position="relative" ref={relativeRef}>
        {variant !== 'dark' ? <Box height={headerHeight} /> : null}
        <Box
          position={isStatic ? 'absolute' : 'fixed'}
          minWidth={320}
          fontFamily="FactorA"
          paddingY={12}
          css={
            isDark
              ? `
              ${headerCss}
            background: rgba(12, 7, 41, 0.01);
            backdrop-filter: blur(16.5px);
            `
              : `
              ${headerCss}
            background: ${active ? '#fff' : 'rgba(255, 255, 255, 0.9)'};
            `
          }
          boxShadow={{ xs: active ? 'strong' : 'none', desktopMenu: 'none' }}
        >
          <header>
            <Block
              // @ts-ignore always leave some space to account for top ad
              maxHeight="80vh"
              overflowY="auto"
            >
              <Row width="100%" space={{ xs: 16, md: 24 }}>
                <Box width="100%" display="flex">
                  <Box
                    flex={1}
                    height={40}
                    paddingRight={headerSafeSpace}
                    display="flex"
                    alignItems="center"
                    css={`
                      a {
                        display: block;
                        line-height: 1;
                      }
                    `}
                  >
                    <Link to="/">
                      <Box.img
                        maxWidth="100%"
                        display={{
                          // Hide logo in narrow mobile menu to give space to CTA buttons
                          xs: active ? 'none' : 'block',
                          sm: 'block',
                        }}
                        src={isDark ? logoFullWhite : logoFull}
                      />
                    </Link>
                  </Box>
                  <Box
                    flexShrink={0}
                    display={{ xs: 'none', desktopMenu: 'block' }}
                  >
                    <DesktopMenu isDark={isDark} />
                  </Box>
                  <Row
                    flex={1}
                    paddingLeft={headerSafeSpace}
                    space={8}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <HeaderButtons isDark={isDark} isMenuActive={active} />
                  </Row>
                </Box>
                <MenuToggle
                  toggleMenu={toggleMenu}
                  active={active}
                  isDark={isDark}
                />
              </Row>
              <Box display={{ xs: 'block', desktopMenu: 'none' }}>
                <MobileMenu active={active} />
              </Box>
            </Block>
          </header>
        </Box>
      </Box>
    </GlobalStyleReset>
  )
}

export default Header

function Banner() {
  const bannerHideDate = new Date('2025-12-26T10:00:00.000+03:00')

  if (bannerHideDate.getTime() <= Date.now()) {
    return null
  }

  return (
    <Box.a
      color="white"
      href="/blog/series-a-funding/"
      target="_self"
      font="small"
      lineHeight="20px"
      fontWeight={500}
      textAlign="center"
      css={`
        text-wrap: balance;
        &:hover {
          color: rgba(255 255 255 / 80%);
        }
      `}
    >
      <Block
        backgroundColor="darkBanner"
        paddingY={16}
        display="flex"
        justifyContent="center"
        maxWidth="100%"
      >
        We raised an $11M Series A led by Karma and DIG Ventures →
      </Block>
    </Box.a>
  )
}
